/**
 * Well-known non-project program definitions
 *
 * Hardcoded display names for common Solana programs that appear in
 * transactions (System Program, Token Programs, etc.).
 * These don't have Anchor IDLs but have well-known instruction layouts.
 */

import { SYSTEM_PROGRAM_ADDRESS } from "@solana-program/system";
import { COMPUTE_BUDGET_PROGRAM_ADDRESS } from "@solana-program/compute-budget";
import {
  TOKEN_PROGRAM_ADDRESS,
  ASSOCIATED_TOKEN_PROGRAM_ADDRESS,
} from "@solana-program/token";
import { TOKEN_2022_PROGRAM_ADDRESS } from "@solana-program/token-2022";

export interface WellKnownInstruction {
  name: string;
  displayName: string;
  description: string;
}

export interface WellKnownProgram {
  programId: string;
  name: string;
  displayName: string;
  /** Keyed by instruction index (u32 LE first 4 bytes) for native programs */
  instructions: Map<number, WellKnownInstruction>;
}

/** System Program instructions (keyed by u32 LE instruction index) */
const SYSTEM_INSTRUCTIONS = new Map<number, WellKnownInstruction>([
  [
    0,
    {
      name: "createAccount",
      displayName: "Create Account",
      description: "Create a new account with lamports",
    },
  ],
  [
    1,
    {
      name: "assign",
      displayName: "Assign",
      description: "Assign account to a program",
    },
  ],
  [
    2,
    {
      name: "transfer",
      displayName: "Transfer SOL",
      description: "Transfer lamports between accounts",
    },
  ],
  [
    3,
    {
      name: "createAccountWithSeed",
      displayName: "Create Account With Seed",
      description: "Create account using a seed",
    },
  ],
  [
    4,
    {
      name: "advanceNonceAccount",
      displayName: "Advance Nonce",
      description: "Advance nonce account",
    },
  ],
  [
    5,
    {
      name: "withdrawNonceAccount",
      displayName: "Withdraw Nonce",
      description: "Withdraw from nonce account",
    },
  ],
  [
    6,
    {
      name: "initializeNonceAccount",
      displayName: "Initialize Nonce",
      description: "Initialize nonce account",
    },
  ],
  [
    7,
    {
      name: "authorizeNonceAccount",
      displayName: "Authorize Nonce",
      description: "Authorize nonce account",
    },
  ],
  [
    8,
    {
      name: "allocate",
      displayName: "Allocate",
      description: "Allocate space for account data",
    },
  ],
  [
    9,
    {
      name: "allocateWithSeed",
      displayName: "Allocate With Seed",
      description: "Allocate space using a seed",
    },
  ],
  [
    10,
    {
      name: "assignWithSeed",
      displayName: "Assign With Seed",
      description: "Assign account using a seed",
    },
  ],
  [
    11,
    {
      name: "transferWithSeed",
      displayName: "Transfer With Seed",
      description: "Transfer lamports using a seed",
    },
  ],
  [
    12,
    {
      name: "upgradeNonceAccount",
      displayName: "Upgrade Nonce",
      description: "Upgrade nonce account",
    },
  ],
]);

/** SPL Token instructions (shared between Token and Token-2022) */
const TOKEN_INSTRUCTIONS = new Map<number, WellKnownInstruction>([
  [
    0,
    {
      name: "initializeMint",
      displayName: "Initialize Mint",
      description: "Initialize a new token mint",
    },
  ],
  [
    1,
    {
      name: "initializeAccount",
      displayName: "Initialize Account",
      description: "Initialize a new token account",
    },
  ],
  [
    2,
    {
      name: "initializeMultisig",
      displayName: "Initialize Multisig",
      description: "Initialize a multisig account",
    },
  ],
  [
    3,
    {
      name: "transfer",
      displayName: "Transfer Tokens",
      description: "Transfer tokens between accounts",
    },
  ],
  [
    4,
    {
      name: "approve",
      displayName: "Approve",
      description: "Approve a delegate for token transfers",
    },
  ],
  [
    5,
    {
      name: "revoke",
      displayName: "Revoke",
      description: "Revoke delegate authority",
    },
  ],
  [
    6,
    {
      name: "setAuthority",
      displayName: "Set Authority",
      description: "Set a new authority on a mint or account",
    },
  ],
  [
    7,
    {
      name: "mintTo",
      displayName: "Mint To",
      description: "Mint new tokens to an account",
    },
  ],
  [
    8,
    {
      name: "burn",
      displayName: "Burn",
      description: "Burn tokens from an account",
    },
  ],
  [
    9,
    {
      name: "closeAccount",
      displayName: "Close Account",
      description: "Close a token account",
    },
  ],
  [
    10,
    {
      name: "freezeAccount",
      displayName: "Freeze Account",
      description: "Freeze a token account",
    },
  ],
  [
    11,
    {
      name: "thawAccount",
      displayName: "Thaw Account",
      description: "Thaw a frozen token account",
    },
  ],
  [
    12,
    {
      name: "transferChecked",
      displayName: "Transfer (Checked)",
      description: "Transfer tokens with decimal verification",
    },
  ],
  [
    13,
    {
      name: "approveChecked",
      displayName: "Approve (Checked)",
      description: "Approve delegate with decimal verification",
    },
  ],
  [
    14,
    {
      name: "mintToChecked",
      displayName: "Mint To (Checked)",
      description: "Mint tokens with decimal verification",
    },
  ],
  [
    15,
    {
      name: "burnChecked",
      displayName: "Burn (Checked)",
      description: "Burn tokens with decimal verification",
    },
  ],
  [
    18,
    {
      name: "syncNative",
      displayName: "Sync Native",
      description: "Sync native SOL balance",
    },
  ],
]);

/** Associated Token Account Program instructions */
const ATA_INSTRUCTIONS = new Map<number, WellKnownInstruction>([
  [
    0,
    {
      name: "create",
      displayName: "Create ATA",
      description: "Create associated token account",
    },
  ],
  [
    1,
    {
      name: "createIdempotent",
      displayName: "Create ATA (Idempotent)",
      description: "Create ATA if it doesn't exist",
    },
  ],
  [
    2,
    {
      name: "recoverNested",
      displayName: "Recover Nested",
      description: "Recover tokens from nested ATA",
    },
  ],
]);

/** Compute Budget Program instructions */
const COMPUTE_BUDGET_INSTRUCTIONS = new Map<number, WellKnownInstruction>([
  [
    0,
    {
      name: "requestHeapFrame",
      displayName: "Request Heap Frame",
      description: "Request additional heap memory",
    },
  ],
  [
    2,
    {
      name: "setComputeUnitLimit",
      displayName: "Set Compute Unit Limit",
      description: "Set maximum compute units for transaction",
    },
  ],
  [
    3,
    {
      name: "setComputeUnitPrice",
      displayName: "Set Compute Unit Price",
      description: "Set priority fee per compute unit",
    },
  ],
]);

/** All well-known programs keyed by program ID. Program IDs come from the
 *  Kit-native `@solana-program/*` packages so they're already base58 strings.
 *  We widen each key to plain `string` because Kit gives each package a
 *  literal-typed `Address<"...">` that TypeScript doesn't auto-union. */
export const WELL_KNOWN_PROGRAMS: Map<string, WellKnownProgram> = new Map<
  string,
  WellKnownProgram
>([
  [
    SYSTEM_PROGRAM_ADDRESS,
    {
      programId: SYSTEM_PROGRAM_ADDRESS,
      name: "system_program",
      displayName: "System Program",
      instructions: SYSTEM_INSTRUCTIONS,
    },
  ],
  [
    TOKEN_PROGRAM_ADDRESS,
    {
      programId: TOKEN_PROGRAM_ADDRESS,
      name: "token_program",
      displayName: "Token Program",
      instructions: TOKEN_INSTRUCTIONS,
    },
  ],
  [
    TOKEN_2022_PROGRAM_ADDRESS,
    {
      programId: TOKEN_2022_PROGRAM_ADDRESS,
      name: "token_2022",
      displayName: "Token-2022",
      instructions: TOKEN_INSTRUCTIONS,
    },
  ],
  [
    ASSOCIATED_TOKEN_PROGRAM_ADDRESS,
    {
      programId: ASSOCIATED_TOKEN_PROGRAM_ADDRESS,
      name: "associated_token_program",
      displayName: "Associated Token Account",
      instructions: ATA_INSTRUCTIONS,
    },
  ],
  [
    COMPUTE_BUDGET_PROGRAM_ADDRESS,
    {
      programId: COMPUTE_BUDGET_PROGRAM_ADDRESS,
      name: "compute_budget",
      displayName: "Compute Budget",
      instructions: COMPUTE_BUDGET_INSTRUCTIONS,
    },
  ],
]);

/**
 * Look up a well-known program by its program ID
 */
export function lookupWellKnownProgram(
  programId: string,
): WellKnownProgram | undefined {
  return WELL_KNOWN_PROGRAMS.get(programId);
}

/**
 * Look up a well-known instruction by program ID and instruction index
 */
export function lookupWellKnownInstruction(
  programId: string,
  instructionIndex: number,
): WellKnownInstruction | undefined {
  return WELL_KNOWN_PROGRAMS.get(programId)?.instructions.get(instructionIndex);
}
